// src/hooks/useAIStickyGeneration.js

import { useState, useCallback } from 'react';
import { useNotes } from '../components/contexts/NotesContext';
import { useStickies } from '../components/contexts/StickiesContext';
import { generateStickiesFromPrompt } from '../api/aiStickyGenerationApi';
import { createStickiesBatch } from '../api/stickiesApi';

const useAIStickyGeneration = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(0);
  
  const { selectedNoteId } = useNotes();
  const { fetchStickies } = useStickies();

  /**
   * Generate stickies from a prompt and create them on the canvas
   */
  const generateAndCreateStickies = useCallback(async (prompt, count = 3) => {
    if (!selectedNoteId) {
      return { success: false, message: 'No note selected' };
    }

    if (!prompt.trim()) {
      return { success: false, message: 'No prompt provided' };
    }

    setIsGenerating(true);
    setError(null);

    try {
      console.log(`Generating ${count} stickies from prompt: "${prompt.substring(0, 100)}..."`);
      
      // Step 1: Call AI API to generate sticky content
      const aiResult = await generateStickiesFromPrompt(prompt, count);
      
      if (!aiResult.stickies || aiResult.stickies.length === 0) {
        return { success: false, message: 'No stickies were generated' };
      }

      console.log(`AI generated ${aiResult.stickies.length} stickies`);
      
      // Step 2: Format stickies for creation (similar to useKeyPointsExtraction.js)
      const stickiesToCreate = [];
      const startX = 40;
      const startY = 70;
      const offsetX = 30;
      const offsetY = 280; // Slightly larger offset for generated content

      aiResult.stickies.forEach((sticky, index) => {
        // Combine title and content into sticky content
        const stickyContent = `${sticky.title.toUpperCase()}\n\n${sticky.content}`;
        
        stickiesToCreate.push({
          content: stickyContent,
          x: startX + (index * offsetX),
          y: startY + (index * offsetY),
          width: 320,  // Slightly wider for generated content
          height: 380, // Taller for more content
          z_index: 1000 + index,
          type: 'regular' 
        });
      });

      console.log(`Creating batch of ${stickiesToCreate.length} stickies`);
      
      // Step 3: Create the actual stickies on canvas
      const createdStickies = await createStickiesBatch(selectedNoteId, stickiesToCreate);
      console.log(`Successfully created ${createdStickies.length} stickies`);

      // Step 4: Refresh stickies display
      fetchStickies(true);

      // Track token usage
      if (aiResult.token_usage) {
        setTokenUsage(aiResult.token_usage);
      }

      return {
        success: true,
        message: `Created ${createdStickies.length} AI-generated sticky notes!`,
        count: createdStickies.length,
        tokenUsage: aiResult.token_usage
      };

    } catch (err) {
      console.error('Error in generateAndCreateStickies:', err);
      const errorMessage = err.message || 'Failed to generate stickies';
      setError(errorMessage);
      
      return { 
        success: false, 
        message: errorMessage 
      };
    } finally {
      setIsGenerating(false);
    }
  }, [selectedNoteId, fetchStickies]);

  /**
   * Generate sticky content only (without creating them) - for preview/testing
   */
  const generateStickiesOnly = useCallback(async (prompt, count = 3) => {
    if (!prompt.trim()) {
      return { success: false, message: 'No prompt provided' };
    }

    setIsGenerating(true);
    setError(null);

    try {
      const result = await generateStickiesFromPrompt(prompt, count);
      
      if (result.token_usage) {
        setTokenUsage(result.token_usage);
      }

      return {
        success: true,
        data: result.stickies,
        count: result.count,
        tokenUsage: result.token_usage
      };
    } catch (err) {
      console.error('Error generating stickies:', err);
      const errorMessage = err.message || 'Failed to generate stickies';
      setError(errorMessage);
      
      return { 
        success: false, 
        message: errorMessage 
      };
    } finally {
      setIsGenerating(false);
    }
  }, []);

  return {
    generateAndCreateStickies,
    generateStickiesOnly,
    isGenerating,
    error,
    tokenUsage
  };
};

export default useAIStickyGeneration;